import React, { Component } from 'react';
import ValidationText from '../../utils/validation/ValidationText';
import { Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeftLong, faCircleNotch, faMinus, faPlus, faTrashCan, faCalculator } from '@fortawesome/free-solid-svg-icons';
import SwalServices from '../../services/swalServices/SwalServices';
import AdminServices from '../../services/axiosServices/apiServices/adminServices';
import { CommonSuccessMessages } from '../../utils/Messages';

class ScoreFormDetails extends Component {
    constructor(props) {
        super(props);
        this.swalServices = new SwalServices();
        this.adminServices = new AdminServices();
        this.state = {
            formId: 0,
            formName: '',
            formDescription: '',
            notificationEmail: '',
            passingMarks: 0,
            sections: [],
            isAnonymous: 0,
            validState: {
                error: {}
            }
        };
    }

    // Calculate total marks based on question type
    // For radio: max marks from single option
    // For checkbox: sum of all options marks (or sum of top N options if maxSelections is set)
    calculateTotalMarks = () => {
        const { sections } = this.state;
        let total = 0;

        sections.forEach(section => {
            section.questions.forEach(question => {
                if (question.options && question.options.length > 0) {
                    if (question.questionType === 'checkbox') {
                        // For checkbox: sum marks of top N options based on maxSelections
                        const sortedMarks = question.options.map(opt => Number(opt.marks) || 0).sort((a, b) => b - a);
                        const maxSelections = question.maxSelections || question.options.length;
                        const marksToSum = sortedMarks.slice(0, maxSelections);
                        total += marksToSum.reduce((sum, mark) => sum + mark, 0);
                    } else {
                        // For radio: get the maximum marks from single option
                        const maxMarks = Math.max(...question.options.map(opt => Number(opt.marks) || 0));
                        total += maxMarks;
                    }
                }
            });
        });

        return total;
    };

    // Calculate max marks for a single question
    calculateQuestionMaxMarks = (question) => {
        if (!question.options || question.options.length === 0) return 0;

        if (question.questionType === 'checkbox') {
            const sortedMarks = question.options.map(opt => Number(opt.marks) || 0).sort((a, b) => b - a);
            const maxSelections = question.maxSelections || question.options.length;
            const marksToSum = sortedMarks.slice(0, maxSelections);
            return marksToSum.reduce((sum, mark) => sum + mark, 0);
        } else {
            return Math.max(...question.options.map(opt => Number(opt.marks) || 0));
        }
    };

    handleChange = (event, field, sectionIndex = null, questionIndex = null) => {
        const value = event.target.value;
        if (sectionIndex === null) {
            this.setState({ [field]: value });
        } else if (questionIndex === null) {
            const updatedSections = [...this.state.sections];
            updatedSections[sectionIndex][field] = value;
            this.setState({ sections: updatedSections });
        } else {
            const updatedSections = [...this.state.sections];
            updatedSections[sectionIndex].questions[questionIndex][field] = value;
            this.setState({ sections: updatedSections });
        }
    };

    addSection = () => {
        const { sections, formName } = this.state;

        if (!formName.trim()) {
            this.swalServices.Error('Please fill the Form Name before adding a section.');
            return;
        }

        if (sections.length > 0) {
            const lastSection = sections[sections.length - 1];
            if (!lastSection.sectionName.trim()) {
                this.swalServices.Error(`Please fill the Section ${sections.length} Name before adding a new section.`);
                return;
            }
        }

        const newSection = {
            sectionName: '',
            secDescription: '',
            questions: []
        };

        this.setState(prevState => ({
            sections: [...prevState.sections, newSection]
        }));
    };

    removeSection = (index) => {
        const updatedSections = [...this.state.sections];
        updatedSections.splice(index, 1);
        this.setState({ sections: updatedSections });
    };

    addQuestion = (sectionIndex) => {
        const { sections } = this.state;
        const section = sections[sectionIndex];

        if (section.questions.length > 0) {
            const lastQuestion = section.questions[section.questions.length - 1];
            if (!lastQuestion.question.trim()) {
                this.swalServices.Error(`Please fill the previous question before adding a new question.`);
                return;
            }
        }

        const newQuestion = {
            question: '',
            questionType: 'radio', // Default to radio for score forms
            maxSelections: 1, // For checkbox: max options user can select
            options: [],
            sortNumber: 1
        };
        const updatedSections = [...sections];
        updatedSections[sectionIndex].questions.push(newQuestion);
        this.setState({ sections: updatedSections });
    };

    removeQuestion = (sectionIndex, questionIndex) => {
        const updatedSections = [...this.state.sections];
        updatedSections[sectionIndex].questions.splice(questionIndex, 1);
        this.setState({ sections: updatedSections });
    };

    handleAddOption = (sectionIndex, questionIndex) => {
        const updatedSections = [...this.state.sections];
        const question = updatedSections[sectionIndex].questions[questionIndex];

        if (question.options.length > 0) {
            const lastOption = question.options[question.options.length - 1];
            if (!lastOption.label.trim()) {
                this.swalServices.Error(`Please fill the previous option before adding a new option.`);
                return;
            }
        }

        // Add option with label and marks
        question.options.push({ label: '', marks: 0 });
        this.setState({ sections: updatedSections });
    };

    removeOption = (sectionIndex, questionIndex, optionIndex) => {
        const updatedSections = [...this.state.sections];
        updatedSections[sectionIndex].questions[questionIndex].options.splice(optionIndex, 1);
        this.setState({ sections: updatedSections });
    };

    handleOptionChange = (e, sectionIndex, questionIndex, optionIndex, field) => {
        const updatedSections = [...this.state.sections];
        const value = field === 'marks' ? Number(e.target.value) || 0 : e.target.value;
        updatedSections[sectionIndex].questions[questionIndex].options[optionIndex][field] = value;
        this.setState({ sections: updatedSections });
    };

    createForm = () => {
        const { formName, formDescription, notificationEmail, isAnonymous, sections, passingMarks } = this.state;

        if (!formName.trim()) {
            this.swalServices.Error("Form name is required.");
            return;
        }

        if (notificationEmail && notificationEmail.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const emails = notificationEmail.split(',').map(email => email.trim()).filter(email => email.length > 0);
            const invalidEmails = emails.filter(email => !emailRegex.test(email));
            if (invalidEmails.length > 0) {
                this.swalServices.Error(`Please enter valid email addresses. Invalid emails: ${invalidEmails.join(', ')}`);
                return;
            }
        }

        if (sections.length === 0 || !sections.some(section => section.questions.length > 0)) {
            this.swalServices.Error("Please add at least one section with one question.");
            return;
        }

        const totalMarks = this.calculateTotalMarks();
        if (passingMarks > totalMarks) {
            this.swalServices.Error(`Passing marks (${passingMarks}) cannot exceed total marks (${totalMarks}).`);
            return;
        }

        if (passingMarks < 0) {
            this.swalServices.Error("Passing marks cannot be negative.");
            return;
        }

        for (let section of sections) {
            if (!section.sectionName.trim()) {
                this.swalServices.Error(`Section name is required in section ${sections.indexOf(section) + 1}.`);
                return;
            }

            for (let question of section.questions) {
                if (!question.question.trim()) {
                    this.swalServices.Error(`Question is required in section ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }

                if (!question.questionType) {
                    this.swalServices.Error(`Question type is required for question in section ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }

                if (!question.options || question.options.length < 2) {
                    this.swalServices.Error(`At least two options are required for question in section ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }

                // Validate that at least one option has marks > 0
                const hasPositiveMarks = question.options.some(opt => opt.marks > 0);
                if (!hasPositiveMarks) {
                    this.swalServices.Error(`At least one option should have marks greater than 0 in section ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }
            }
        }

        const payload = {
            isAnonymous,
            formName,
            formDescription,
            formType: 'score', // Mark this as a score form
            passingMarks: Number(passingMarks),
            totalMarks: totalMarks,
            notificationEmail: notificationEmail.trim() || null,
            questionDetails: sections.map(section => ({
                sectionName: section.sectionName,
                secDescription: section.secDescription,
                questionData: section.questions.map(question => ({
                    question: question.question,
                    questionType: question.questionType,
                    maxSelections: question.questionType === 'checkbox' ? (question.maxSelections || question.options.length) : 1,
                    sortNumber: question.sortNumber,
                    options: question.options.map(opt => ({
                        label: opt.label,
                        marks: opt.marks
                    }))
                }))
            }))
        };

        this.setState({ isLoading: true });
        this.adminServices.createScoreForm(payload).then((response) => {
            if (response.success === 1) {
                this.setState({ route: "/form-list" });
                this.swalServices.Success(response.msg);
            } else {
                this.swalServices.Error(response.msg);
            }
            this.setState({ isLoading: false });
        });
    };

    updateForm = () => {
        const { formName, formDescription, notificationEmail, isAnonymous, sections, passingMarks } = this.state;

        if (!formName.trim()) {
            this.swalServices.Error("Form name is required.");
            return;
        }

        if (notificationEmail && notificationEmail.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const emails = notificationEmail.split(',').map(email => email.trim()).filter(email => email.length > 0);
            const invalidEmails = emails.filter(email => !emailRegex.test(email));
            if (invalidEmails.length > 0) {
                this.swalServices.Error(`Please enter valid email addresses. Invalid emails: ${invalidEmails.join(', ')}`);
                return;
            }
        }

        if (sections.length === 0 || !sections.some(section => section.questions.length > 0)) {
            this.swalServices.Error("Please add at least one section with one question.");
            return;
        }

        const totalMarks = this.calculateTotalMarks();
        if (passingMarks > totalMarks) {
            this.swalServices.Error(`Passing marks (${passingMarks}) cannot exceed total marks (${totalMarks}).`);
            return;
        }

        if (passingMarks < 0) {
            this.swalServices.Error("Passing marks cannot be negative.");
            return;
        }

        for (let section of sections) {
            if (!section.sectionName.trim()) {
                this.swalServices.Error(`Section name is required in section ${sections.indexOf(section) + 1}.`);
                return;
            }

            for (let question of section.questions) {
                if (!question.question.trim()) {
                    this.swalServices.Error(`Question is required in section ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }

                if (!question.questionType) {
                    this.swalServices.Error(`Question type is required for question in section ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }

                if (!question.options || question.options.length < 2) {
                    this.swalServices.Error(`At least two options are required for question in section ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }
            }
        }

        const payload = {
            isAnonymous,
            formName,
            formDescription,
            formType: 'score',
            passingMarks: Number(passingMarks),
            totalMarks: totalMarks,
            notificationEmail: notificationEmail.trim() ? notificationEmail.split(',').map(email => email.trim()).filter(email => email.length > 0).join(',') : "",
            questionDetails: sections.map(section => ({
                sectionName: section.sectionName,
                secDescription: section.secDescription,
                questionData: section.questions.map(question => ({
                    question: question.question,
                    questionType: question.questionType,
                    maxSelections: question.questionType === 'checkbox' ? (question.maxSelections || question.options.length) : 1,
                    sortNumber: question.sortNumber,
                    options: question.options.map(opt => ({
                        label: opt.label,
                        marks: opt.marks
                    }))
                }))
            }))
        };

        this.setState({ isLoading: true });
        this.adminServices.updateScoreForm(payload, this.state.formId).then((response) => {
            if (response.success === 1) {
                this.setState({ route: "/form-list" });
                this.swalServices.Success(response.msg);
            } else {
                this.swalServices.Error(response.msg);
            }
            this.setState({ isLoading: false });
        });
    };

    handleDelete = () => {
        this.swalServices.Confirm(
            "",
            ``,
            CommonSuccessMessages.Yes_Text,
            CommonSuccessMessages.No_Text,
            `Are you sure you want to delete ?`
        ).then((response) => {
            if (response === true) {
                this.setState({ isLoading: true });
                let id = this.state.formId;
                this.adminServices.deleteScoreForm(id).then((response) => {
                    if (response.success === 1) {
                        this.swalServices.Success(response.msg);
                        setTimeout(() => {
                            this.setState({ route: "/form-list" });
                        }, 1000);
                    } else {
                        this.swalServices.Error(response.msg);
                        this.setState({ isLoading: false });
                    }
                });
            }
        });
    };

    componentDidMount() {
        let params = new URLSearchParams(window.location.search);
        let formId = Number(params.get(""));
        if (formId) {
            if (formId != null && formId > 0) {
                this.setState({ formId: formId }, () => {
                    this.getFormData(this.state.formId);
                });
            }
        }
    }

    getFormData = (id) => {
        this.adminServices.getScoreFormData(id).then((response) => {
            if (response.success === 1 && response.data !== null) {
                const { formName, formDescription, notificationEmail, isAnonymous, passingMarks, questionDetails } = response.data;

                const sections = questionDetails.map(section => ({
                    sectionName: section.sectionName,
                    secDescription: section.secDescription,
                    questions: section?.questionData?.map(q => ({
                        question: q.question,
                        questionType: q.questionType,
                        maxSelections: q.maxSelections || (q.options ? q.options.length : 1),
                        sortNumber: q.sortNumber,
                        options: q.options || []
                    }))
                }));

                this.setState({
                    formName: formName,
                    formDescription: formDescription,
                    notificationEmail: notificationEmail || '',
                    isAnonymous: isAnonymous,
                    passingMarks: passingMarks || 0,
                    sections: sections
                });

            } else {
                this.swalServices.Error(response.msg);
            }
        }).catch((error) => {
            console.error("Error fetching form data:", error);
            this.swalServices.Error("An error occurred while fetching form data.");
        });
    };

    render() {
        if (this.state.route != null) {
            return <Navigate to={this.state.route} />;
        }
        const { formName, formDescription, sections, validState, passingMarks } = this.state;
        const totalMarks = this.calculateTotalMarks();

        return (
            <div className='form-details'>
                <div className='form-details-inner p-[30px] overflow-y-auto' style={{ height: "calc(100vh - 168.8px)" }}>
                    <div className='title flex items-center justify-between mb-[10px]'>
                        <h1 className='text-[22px] m-0 font-semibold text-[#004AAD]' style={{ lineHeight: "30px" }}>
                            {this.state.formId > 0 ? 'Edit Assessment Form' : 'Create Assessment Form'}
                        </h1>
                    </div>
                    <div className='form-data grid gap-[30px]'>
                        <div className='form-info p-5 bg-white rounded-[16px]' style={{ border: "1px solid #DFDFDF" }}>
                            <div className='flex items-center justify-between mb-5'>
                                <h2 className='text-[16px] font-semibold mt-0'>Assessment Form Details</h2>
                                <label className='text-[16px] mt-0 flex flex-row-reverse items-center gap-2'>
                                    Anonymous
                                    <input
                                        type='checkbox'
                                        className='w-5 h-5 cursor-pointer'
                                        checked={this.state.isAnonymous === 1}
                                        onChange={(e) => this.setState({ isAnonymous: e.target.checked ? 1 : 0 })}
                                        value={this.state.isAnonymous}
                                    />
                                </label>
                            </div>

                            <div className="dynamic-form-builder">
                                <div className="grid grid-cols-2 gap-5">
                                    <div className="input-grp">
                                        <label>Form Name<span className='req'>*</span></label>
                                        <input
                                            type="text"
                                            placeholder="Enter form name"
                                            className={`${validState?.error?.formName ? 'error' : ''}`}
                                            value={formName}
                                            onChange={(e) => this.handleChange(e, "formName")}
                                        />
                                        <ValidationText error={validState.error.formName} />
                                    </div>

                                    <div className="input-grp">
                                        <label>Form Description:</label>
                                        <textarea
                                            rows="3"
                                            placeholder="Enter form description"
                                            className={`${validState?.error?.formDescription ? 'error' : ''}`}
                                            value={formDescription}
                                            onChange={(e) => this.handleChange(e, "formDescription")}
                                            style={{ resize: 'vertical', minHeight: '80px' }}
                                        />
                                        <ValidationText error={validState.error.formDescription} />
                                    </div>
                                </div>

                                <div className="input-grp col-span-2 mt-4">
                                    <label>Notification Email (Optional):</label>
                                    <input
                                        type="email"
                                        placeholder="Enter email addresses (comma-separated) for form submission notifications"
                                        className={`${validState?.error?.notificationEmail ? 'error' : ''}`}
                                        value={this.state.notificationEmail}
                                        onChange={(e) => this.handleChange(e, "notificationEmail")}
                                    />
                                    <ValidationText error={validState.error.notificationEmail} />
                                </div>

                                {sections.map((section, sectionIndex) => (
                                    <div key={sectionIndex} className="section-block">
                                        <hr className='my-5' />

                                        <div className="section-header">
                                            <div className='btn-left mt-5 mb-5'>
                                                <button
                                                    onClick={() => { this.removeSection(sectionIndex) }}
                                                    className='font-medium text-[12px] text-[#ffffff] leading-[24px] px-[15px] py-[8px] rounded-[14px] bg-[#EA3636] hover:bg-[#c53030] border-0 flex items-center gap-[10px]'>
                                                    <FontAwesomeIcon icon={faMinus} className='w-3 h-3' />Section {sectionIndex + 1}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-5">
                                            <div className="input-grp">
                                                <label>Section Name <span className='req'>*</span></label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter section name"
                                                    value={section.sectionName}
                                                    onChange={(e) => this.handleChange(e, "sectionName", sectionIndex)}
                                                />
                                            </div>

                                            <div className="input-grp">
                                                <label>Section Description:</label>
                                                <textarea
                                                    rows="2"
                                                    placeholder="Enter section description"
                                                    value={section.secDescription}
                                                    onChange={(e) => this.handleChange(e, "secDescription", sectionIndex)}
                                                    style={{ resize: 'vertical', minHeight: '60px' }}
                                                />
                                            </div>
                                        </div>

                                        {section.questions.map((question, questionIndex) => (
                                            <div key={questionIndex} className="question-block bg-gray-50 p-4 rounded-[12px] mt-4 border border-gray-200">
                                                <div className='flex items-center justify-between mb-4'>
                                                    <span className='text-[14px] font-semibold text-[#004AAD]'>Question {questionIndex + 1}</span>
                                                    <button
                                                        onClick={() => { this.removeQuestion(sectionIndex, questionIndex) }}
                                                        className='font-medium text-[12px] text-[#EA3636] hover:text-[#ffffff] leading-[24px] px-[15px] py-[6px] rounded-[10px] bg-[#ffe8e8] hover:bg-[#EA3636] border-0 flex items-center gap-[8px]'>
                                                        <FontAwesomeIcon icon={faMinus} className='w-3 h-3' />Remove
                                                    </button>
                                                </div>

                                                <div className="input-grp">
                                                    <label>Question Text<span className='req'>*</span></label>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter question"
                                                        value={question.question}
                                                        onChange={(e) => this.handleChange(e, "question", sectionIndex, questionIndex)}
                                                    />
                                                </div>

                                                <div className='grid grid-cols-3 gap-5 mt-3'>
                                                    <div className="input-grp !mb-0">
                                                        <label>Question Type<span className='req'>*</span></label>
                                                        <select
                                                            value={question.questionType}
                                                            onChange={(e) => {
                                                                const updatedSections = [...this.state.sections];
                                                                updatedSections[sectionIndex].questions[questionIndex].questionType = e.target.value;
                                                                // Reset maxSelections when switching to radio
                                                                if (e.target.value === 'radio') {
                                                                    updatedSections[sectionIndex].questions[questionIndex].maxSelections = 1;
                                                                }
                                                                this.setState({ sections: updatedSections });
                                                            }}
                                                        >
                                                            <option value="radio">Radio (Single Select)</option>
                                                            <option value="checkbox">Checkbox (Multiple Select)</option>
                                                        </select>
                                                    </div>

                                                    {question.questionType === 'checkbox' && (
                                                        <div className="input-grp !mb-0">
                                                            <label>Max Selections<span className='req'>*</span></label>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                max={question.options.length || 10}
                                                                placeholder="Max options to select"
                                                                value={question.maxSelections || ''}
                                                                onChange={(e) => {
                                                                    const updatedSections = [...this.state.sections];
                                                                    updatedSections[sectionIndex].questions[questionIndex].maxSelections = Number(e.target.value) || 1;
                                                                    this.setState({ sections: updatedSections });
                                                                }}
                                                            />
                                                            <small className='text-gray-500 text-[10px]'>How many options can user select</small>
                                                        </div>
                                                    )}

                                                    <div className="input-grp !mb-0">
                                                        <label>Sort Number:</label>
                                                        <input
                                                            type="number"
                                                            placeholder="Enter sort number"
                                                            value={question.sortNumber}
                                                            onChange={(e) => this.handleChange(e, "sortNumber", sectionIndex, questionIndex)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Options with Marks */}
                                                <div className="options-block mt-4">
                                                    <div className='flex items-center justify-between mb-3'>
                                                        <h5 className='text-[14px] font-semibold m-0'>Options with Marks<span className='req'>*</span></h5>
                                                        <span className='text-[12px] text-gray-500 bg-blue-100 px-2 py-1 rounded'>
                                                            Max marks for this question: <strong>{this.calculateQuestionMaxMarks(question)}</strong>
                                                            {question.questionType === 'checkbox' && question.maxSelections && (
                                                                <span className='ml-1'>(sum of top {question.maxSelections} options)</span>
                                                            )}
                                                        </span>
                                                    </div>

                                                    {question.options.map((option, optionIndex) => (
                                                        <div key={optionIndex} className="flex gap-3 items-center mb-2">
                                                            <div className='flex-1'>
                                                                <input
                                                                    type="text"
                                                                    placeholder="Enter option label"
                                                                    value={option.label}
                                                                    onChange={(e) => this.handleOptionChange(e, sectionIndex, questionIndex, optionIndex, 'label')}
                                                                    className='w-full'
                                                                />
                                                            </div>
                                                            <div className='w-[120px]'>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    placeholder="Marks"
                                                                    value={option.marks}
                                                                    onChange={(e) => this.handleOptionChange(e, sectionIndex, questionIndex, optionIndex, 'marks')}
                                                                    className='w-full text-center !border-[#10B981]'
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => this.removeOption(sectionIndex, questionIndex, optionIndex)}
                                                                className="text-[#EA3636] hover:text-[#ffffff] rounded-[8px] border-0 bg-[#ffe8e8] hover:bg-[#EA3636] p-2.5 w-fit h-fit"
                                                            >
                                                                <FontAwesomeIcon icon={faMinus} className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ))}

                                                    <div className='btn-left mt-3'>
                                                        <button
                                                            onClick={() => { this.handleAddOption(sectionIndex, questionIndex) }}
                                                            className='font-medium text-[12px] text-green-500 leading-[24px] px-[15px] py-[8px] rounded-[14px] bg-transparent border-2 border-green-500 hover:bg-green-100 flex items-center gap-[10px]'
                                                        >
                                                            <FontAwesomeIcon icon={faPlus} className='w-3 h-3' />
                                                            Add Option
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        <div className='btn-left mt-5'>
                                            <button
                                                onClick={() => { this.addQuestion(sectionIndex) }}
                                                className='font-medium text-[12px] text-[#fff] leading-[24px] px-[15px] py-[8px] rounded-[14px] bg-green-500 hover:bg-green-600 border-0 flex items-center gap-[10px]'>
                                                <FontAwesomeIcon icon={faPlus} className='w-3 h-3' />Question
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                <div className='btn-left mt-5'>
                                    <button
                                        onClick={() => { this.addSection() }}
                                        className='font-medium text-[12px] text-[#fff] leading-[24px] px-[15px] py-[8px] rounded-[14px] bg-[#0077B6] hover:bg-[#3b82f6] border-0 flex items-center gap-[10px]'
                                    >
                                        <FontAwesomeIcon icon={faPlus} className='w-3 h-3' />
                                        Section
                                    </button>
                                </div>
                            </div>
                            <br></br>
                            <br></br>
                            <hr/>
                            {/* Score Summary Box */}
                            <div className="mt-5 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-[12px] border border-blue-200">
                                <div className='flex items-center gap-2 mb-3'>
                                    <FontAwesomeIcon icon={faCalculator} className='text-[#0077B6]' />
                                    <h3 className='text-[16px] font-semibold text-[#004AAD] m-0'>Scoring Configuration</h3>
                                </div>
                                <div className='grid grid-cols-3 gap-5'>
                                    <div className="input-grp !mb-0">
                                        {/* <label className='text-[14px] font-medium text-gray-700'>Total Marks (Auto-calculated)</label> */}
                                        <div className='bg-white px-4 py-3 rounded-[8px] border border-gray-300 text-[18px] font-bold text-[#10B981]'>
                                            {totalMarks}
                                        </div>
                                        <small className='text-gray-500 text-[11px]'>Sum of maximum marks from each question</small>
                                    </div>
                                    <div className="input-grp !mb-0">
                                        <label className='text-[14px] font-medium text-gray-700'>Passing Marks<span className='req'>*</span></label>
                                        <input
                                            type="number"
                                            min="0"
                                            max={totalMarks}
                                            placeholder="Enter passing marks"
                                            value={passingMarks}
                                            onChange={(e) => this.setState({ passingMarks: Number(e.target.value) || 0 })}
                                            className='!border-[#0077B6]'
                                        />
                                        <small className='text-gray-500 text-[11px]'>Minimum marks required to pass</small>
                                    </div>
                                    <div className="input-grp !mb-0">
                                        <label className='text-[14px] font-medium text-gray-700'>Pass Percentage</label>
                                        <div className='bg-white px-4 py-3 rounded-[8px] border border-gray-300 text-[18px] font-bold text-[#0077B6]'>
                                            {totalMarks > 0 ? ((passingMarks / totalMarks) * 100).toFixed(1) : 0}%
                                        </div>
                                        <small className='text-gray-500 text-[11px]'>Percentage required to pass</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='sticky bottom-0 bg-white border-t border-slate-200 z-30 px-[30px] py-[30px] flex items-center justify-between' style={{ borderTop: "1px solid rgb(0,0,0,0.1)", boxShadow: "0 -7px 12px -6px rgb(14, 63, 126, 0.04)" }}>
                    <div className='btn-left'>
                        <button
                            onClick={() => { this.setState({ route: "/form-list" }) }}
                            className='font-medium text-[16px] text-[#223645] leading-[24px] px-[24px] py-[12px] rounded-[14px] bg-[#EBEBEB] hover:bg-[#dddddd] border-0 flex items-center gap-[5px]'>
                            <FontAwesomeIcon icon={faArrowLeftLong} className='w-5 h-5' />Back
                        </button>
                    </div>
                    <div className='btn-right flex items-center gap-5'>
                        {this.state.formId > 0 ?
                            <>
                                <button
                                    onClick={() => this.handleDelete()}
                                    className='font-medium text-[16px] text-[#EA3636] hover:text-[#ffffff] leading-[24px] px-[24px] py-[12px] rounded-[14px] border-0 bg-[#ffe8e8] hover:bg-[#EA3636] flex items-center gap-[5px]'>
                                    <FontAwesomeIcon icon={faTrashCan} className='w-5 h-5' />Delete
                                </button>
                                <button
                                    onClick={() => this.updateForm()}
                                    className='font-medium text-[16px] text-[#FFFFFF] leading-[24px] px-[24px] py-[12px] rounded-[14px] bg-[#0077B6] hover:bg-[#3b82f6] border-0'>
                                    {this.state.isLoading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : "Update"}
                                </button>
                            </>
                            :
                            <button
                                onClick={() => this.createForm()}
                                className='font-medium text-[16px] text-[#FFFFFF] leading-[24px] px-[24px] py-[12px] rounded-[14px] bg-[#10B981] hover:bg-[#059669] border-0'>
                                {this.state.isLoading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : "Save Assessment"}
                            </button>
                        }
                    </div>
                </div>
            </div>
        );
    }
}

export default ScoreFormDetails;
